// src/api/docenteApi.js
// Servicio para comunicación con el backend de horarios y asistencia
// ✅ ACTUALIZADO: Soporte para timezone optimizado

import { exigirPeriodoEscolar, parametrosHorarios } from '../utils/periodoEscolar';
import { aplicarCredencialDocente, docenteParaFecha } from '../utils/credencialDocente';
import { validarApiTest } from '../utils/entorno';
import { fetchConSesionDocente } from '../utils/sesionDocente';
import { normalizarCedulaDocente } from '../utils/cedulaDocente';

const API_BASE_URL = validarApiTest(import.meta.env.VITE_STAGE, import.meta.env.VITE_API_BASE_URL);

const getToken = () => {
  return localStorage.getItem('docente_token');
};

const getAuthHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const fetchDocente = (url, options) => fetchConSesionDocente(url, options, {
  fetcher: fetch,
  storage: localStorage,
  redirect: path => window.location.replace(path),
});

// ==================== AUTENTICACIÓN ====================

export const docenteApi = {
  login: async (cedula, email, colegioID = null) => {  // ✅ MODIFICADO: Agregar email
    const response = await fetchDocente(`${API_BASE_URL}/docente/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cedula: normalizarCedulaDocente(cedula), email, ...(colegioID && { colegioID }) })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error en login');
    }
    
    return response.json();
  },

  getColegios: async (cedula) => {
    const params = new URLSearchParams({ cedula: normalizarCedulaDocente(cedula) });
    const response = await fetchDocente(`${API_BASE_URL}/docente/colegios?${params}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error obteniendo colegios');
    }
    
    return response.json();
  },

  verificarToken: async () => {
    const response = await fetchDocente(`${API_BASE_URL}/docente/verificar`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      if (response.status !== 401) throw new Error('No se pudo validar la sesión. Inténtalo nuevamente.');
      return null;
    }
    
    return response.json();
  },

  cambiarColegio: async (cedula, email, nuevoColegioID, tokenAnterior) => {  // ✅ MODIFICADO: Agregar email
    const response = await fetchDocente(`${API_BASE_URL}/docente/cambiar-colegio`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cedula: normalizarCedulaDocente(cedula), email, nuevoColegioID, tokenAnterior })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error cambiando colegio');
    }
    
    return response.json();
  },

  logout: async () => {
    const response = await fetchDocente(`${API_BASE_URL}/docente/logout`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    
    localStorage.removeItem('docente_token');
    localStorage.removeItem('docente_data');
    localStorage.removeItem('colegio_seleccionado');
    localStorage.removeItem('pais_code');      // ✅ NUEVO
    localStorage.removeItem('time_zone');      // ✅ NUEVO
    
    if (!response.ok) {
      console.warn('Error en logout del servidor, pero sesión local limpiada');
    }
    
    return response.ok;
  }
};

// ==================== HORARIOS ====================

export const horariosApi = {
  getHorariosHoy: async (docenteID, colegioID, anioEscolar = null, fecha = null) => {
    // ✅ NUEVO: Obtener timezone para enviar en query params
    const sesion = authHelpers.getSesion();
    
    const params = parametrosHorarios(docenteParaFecha(docenteID, colegioID, fecha, sesion), colegioID, anioEscolar);
    if (fecha) params.append('fecha', fecha);
    
    // ✅ NUEVO: Incluir timezone si está disponible
    if (sesion?.timeZone) params.append('timeZone', sesion.timeZone);
    if (sesion?.paisCode) params.append('paisCode', sesion.paisCode);
    
    const response = await fetchDocente(`${API_BASE_URL}/asistencia-clases/horarios-hoy?${params}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error obteniendo horarios');
    }
    
    return response.json();
  },

  getHorariosDocente: async (docenteID, colegioID) => {
    const params = new URLSearchParams({ docenteID, colegioID, soloActivos: 'true' });

    const response = await fetchDocente(`${API_BASE_URL}/horarios?${params}`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error obteniendo horarios');
    }

    return response.json();
  },

  // Devuelve las clases del docente para toda la semana (sin filtro por día actual)
  getHorariosSemana: async (docenteID, colegioID, anioEscolar = null) => {
    const sesion = authHelpers.getSesion();

    const params = parametrosHorarios(docenteID, colegioID, anioEscolar);
    params.set('todosDias', 'true');

    if (sesion?.timeZone) params.append('timeZone', sesion.timeZone);
    if (sesion?.paisCode) params.append('paisCode', sesion.paisCode);

    const response = await fetchDocente(`${API_BASE_URL}/asistencia-clases/horarios-hoy?${params}`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error obteniendo horario semanal');
    }

    return response.json();
  }
};

// ==================== ASISTENCIA ====================

export const asistenciaApi = {
  // Conteos (marcados/total) de cada clase del docente para una fecha.
  // Una sola llamada batch → respuesta { conteos: [{ horarioID, total, marcados }] }
  getConteosDia: async (docenteID, colegioID, anioEscolar, fecha = null) => {
    exigirPeriodoEscolar(anioEscolar);
    const sesion = authHelpers.getSesion();
    const params = new URLSearchParams({ docenteID: docenteParaFecha(docenteID, colegioID, fecha, sesion), colegioID, anioEscolar });
    if (fecha) params.append('fecha', fecha);
    if (sesion?.timeZone) params.append('timeZone', sesion.timeZone);
    if (sesion?.paisCode) params.append('paisCode', sesion.paisCode);

    const response = await fetchDocente(`${API_BASE_URL}/asistencia-clases/conteos-dia?${params}`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error obteniendo conteos del día');
    }

    return response.json();
  },

  // ✅ ACTUALIZADO: Agregar docenteID y timezone para bloques compartidos
  getEstudiantesClase: async (colegioID, anioEscolar, nivelAcademico, seccion, horarioID, docenteID, fecha = null) => {
    exigirPeriodoEscolar(anioEscolar);
    const sesion = authHelpers.getSesion();
    const params = new URLSearchParams({
      colegioID,
      anioEscolar,
      nivelAcademico,
      seccion,
      horarioID,
      docenteID: docenteParaFecha(docenteID, colegioID, fecha, sesion),
      ...(fecha && { fecha }),
      ...(sesion?.timeZone && { timeZone: sesion.timeZone }),
      ...(sesion?.paisCode && { paisCode: sesion.paisCode }),
    });
    
    const response = await fetchDocente(`${API_BASE_URL}/asistencia-clases/estudiantes-clase?${params}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error obteniendo estudiantes');
    }
    
    return response.json();
  },

  marcarAsistencia: async (horarioID, colegioID, docenteID, fecha, estudianteID, estado, anioEscolar, observaciones = '') => {
    exigirPeriodoEscolar(anioEscolar);
    const sesion = authHelpers.getSesion();
    const paisCode = sesion?.paisCode;
    const timeZone = sesion?.timeZone;

    const response = await fetchDocente(`${API_BASE_URL}/asistencia-clases/marcar`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        horarioID,
        colegioID,
        docenteID: docenteParaFecha(docenteID, colegioID, fecha, sesion),
        fecha,
        estudianteID,
        estado,
        anioEscolar,
        observaciones,
        paisCode,
        timeZone,
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error marcando asistencia');
    }
    
    return response.json();
  },

  marcarAsistenciaBatch: async (horarioID, colegioID, docenteID, fecha, asistencias, anioEscolar) => {
    exigirPeriodoEscolar(anioEscolar);
    // ✅ NUEVO: Obtener timezone de la sesión
    const sesion = authHelpers.getSesion();
    const paisCode = sesion?.paisCode;
    const timeZone = sesion?.timeZone;

    const response = await fetchDocente(`${API_BASE_URL}/asistencia-clases/marcar`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        horarioID,
        colegioID,
        docenteID: docenteParaFecha(docenteID, colegioID, fecha, sesion),
        fecha,
        asistencias,
        anioEscolar,
        paisCode,    // ✅ NUEVO
        timeZone     // ✅ NUEVO
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error marcando asistencias');
    }
    
    return response.json();
  },

  getEstudiantesConjuntas: async (colegioID, anioEscolar, fecha, clases) => {
    exigirPeriodoEscolar(anioEscolar);
    const params = new URLSearchParams({ colegioID, anioEscolar, fecha, clases: JSON.stringify(clases) });
    const response = await fetchDocente(`${API_BASE_URL}/asistencia-clases/estudiantes-conjuntas?${params}`, { headers: getAuthHeaders() });
    if (!response.ok) { const error = await response.json(); throw new Error(error.message || 'Error obteniendo estudiantes'); }
    return response.json();
  },

  marcarAsistenciaConjunta: async (colegioID, docenteID, fecha, clases, anioEscolar) => {
    exigirPeriodoEscolar(anioEscolar);
    const sesion = authHelpers.getSesion();
    const response = await fetchDocente(`${API_BASE_URL}/asistencia-clases/marcar`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ colegioID, docenteID: docenteParaFecha(docenteID, colegioID, fecha, sesion), fecha, clases, anioEscolar, paisCode: sesion?.paisCode, timeZone: sesion?.timeZone })
    });
    if (!response.ok) { const error = await response.json(); throw new Error(error.message || 'Error marcando asistencia conjunta'); }
    return response.json();
  },

  actualizarAsistencia: async (horarioID, fecha, estudianteID, nuevoEstado, observaciones = '', modificadoPor) => {
    const response = await fetchDocente(`${API_BASE_URL}/asistencia-clases/actualizar`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        horarioID,
        fecha,
        estudianteID,
        nuevoEstado,
        observaciones,
        modificadoPor
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error actualizando asistencia');
    }
    
    return response.json();
  },

  getAsistenciaClase: async (horarioID, fecha, contexto = {}) => {
    const sesion = await authHelpers.refrescarSesion();
    if (!sesion?.token || !sesion.colegioID) {
      throw new Error('Sesión no válida. Vuelve a ingresar.');
    }
    const asegurarSesion = () => {
      const actual = authHelpers.getSesion();
      if (actual?.token !== sesion.token || actual?.colegioID !== sesion.colegioID ||
          (contexto?.colegioID && contexto.colegioID !== sesion.colegioID)) {
        throw new Error('La institución cambió. Actualiza la vista antes de continuar.');
      }
    };
    asegurarSesion();
    const params = new URLSearchParams({ horarioID, fecha, colegioID: sesion.colegioID });
    if (contexto?.anioEscolar) params.set('anioEscolar', exigirPeriodoEscolar(contexto.anioEscolar));

    const response = await fetchDocente(`${API_BASE_URL}/asistencia-clases/asistencia-clase?${params}`, {
      headers: { ...getAuthHeaders(), Authorization: `Bearer ${sesion.token}` }
    });
    const resultado = await response.json();
    asegurarSesion();
    if (!response.ok) {
      throw new Error(resultado.message || 'Error obteniendo asistencia');
    }

    return resultado;
  }
};

// ==================== MATERIAS ====================

export const materiasApi = {
  getMaterias: async (colegioID, soloActivas = true) => {
    const params = new URLSearchParams({ 
      colegioID,
      soloActivas: soloActivas.toString()
    });
    
    const response = await fetchDocente(`${API_BASE_URL}/materias?${params}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error obteniendo materias');
    }
    
    return response.json();
  },

  crearMateria: async (colegioID, nombreMateria, docenteCedula, creadoPor = 'admin') => {
    const response = await fetchDocente(`${API_BASE_URL}/materias`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        colegioID,
        nombreMateria,
        docenteCedula,
        creadoPor
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error creando materia');
    }
    
    return response.json();
  },

  actualizarMateria: async (colegioID, materiaID, nombreMateria, docenteCedula, modificadoPor = 'admin') => {
    const response = await fetchDocente(`${API_BASE_URL}/materias`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        colegioID,
        materiaID,
        nombreMateria,
        docenteCedula,
        modificadoPor
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error actualizando materia');
    }
    
    return response.json();
  },

  eliminarMateria: async (colegioID, materiaID, eliminadoPor = 'admin') => {
    const params = new URLSearchParams({ 
      colegioID, 
      materiaID,
      modificadoPor: eliminadoPor 
    });
    
    const response = await fetchDocente(`${API_BASE_URL}/materias?${params}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error eliminando materia');
    }
    
    return response.json();
  }
};

// ==================== HORARIOS GRID ====================

export const horariosGridApi = {
  getGrid: async (colegioID, anioEscolar, nivelAcademico, seccion) => {
    const params = new URLSearchParams({
      colegioID,
      anioEscolar,
      nivelAcademico,
      seccion
    });
    
    const response = await fetchDocente(`${API_BASE_URL}/horarios-grid?${params}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error obteniendo grid');
    }
    
    return response.json();
  },

  guardarCelda: async (colegioID, anioEscolar, nivelAcademico, seccion, dia, horaSlot, materiaID, modificadoPor = 'admin', horaInicio, horaFin) => {
    const response = await fetchDocente(`${API_BASE_URL}/horarios-grid`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        colegioID,
        anioEscolar,
        nivelAcademico,
        seccion,
        dia,
        horaSlot,
        materiaID,
        modificadoPor,
        horaInicio,
        horaFin
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error guardando celda');
    }
    
    return response.json();
  },

  eliminarCelda: async (colegioID, anioEscolar, nivelAcademico, seccion, dia, horaSlot, modificadoPor = 'admin') => {
    const params = new URLSearchParams({
      colegioID,
      anioEscolar,
      nivelAcademico,
      seccion,
      dia,
      horaSlot,
      modificadoPor
    });
    
    const response = await fetchDocente(`${API_BASE_URL}/horarios-grid?${params}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error eliminando celda');
    }
    
    return response.json();
  }
};

// ==================== HELPERS ====================

const verificaciones = new Map();
export const authHelpers = {
  refrescarSesion: async () => {
    const sesion = authHelpers.getSesion();
    if (!sesion) return null;
    const clave = JSON.stringify([sesion.token, sesion.colegioID]);
    if (!verificaciones.has(clave)) {
      const consulta = docenteApi.verificarToken().then((respuesta) => {
        if (!respuesta?.valido) throw new Error('Sesión no válida. Vuelve a ingresar.');
        aplicarCredencialDocente(respuesta, sesion.token, sesion.colegioID);
      }).finally(() => verificaciones.delete(clave));
      verificaciones.set(clave, consulta);
    }
    await verificaciones.get(clave);
    const actual = authHelpers.getSesion();
    if (actual?.token !== sesion.token || actual?.colegioID !== sesion.colegioID) {
      throw new Error('La institución cambió. Actualiza la vista antes de continuar.');
    }
    return actual;
  },
  guardarSesion: (token, docenteData, colegioID, paisCode, timeZone) => {
    localStorage.setItem('docente_token', token);
    localStorage.setItem('docente_data', JSON.stringify(docenteData));
    localStorage.setItem('colegio_seleccionado', colegioID);
    localStorage.setItem('pais_code', paisCode || 'VE');
    localStorage.setItem('time_zone', timeZone || 'America/Caracas');
  },

  // Lista de colegios del docente (se guarda al login para evitar pegar a la red)
  guardarColegios: (colegios) => {
    if (Array.isArray(colegios)) {
      localStorage.setItem('docente_colegios', JSON.stringify(colegios));
    }
  },

  getColegios: () => {
    try {
      const data = localStorage.getItem('docente_colegios');
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  },

  getSesion: () => {
    const token = localStorage.getItem('docente_token');
    const docenteData = localStorage.getItem('docente_data');
    const colegioID = localStorage.getItem('colegio_seleccionado');
    const paisCode = localStorage.getItem('pais_code');
    const timeZone = localStorage.getItem('time_zone');

    if (!token || !docenteData) return null;

    return {
      token,
      docente: JSON.parse(docenteData),
      colegioID,
      paisCode: paisCode || 'VE',
      timeZone: timeZone || 'America/Caracas'
    };
  },

  haySesionActiva: () => {
    return !!localStorage.getItem('docente_token');
  },

  limpiarSesion: () => {
    localStorage.removeItem('docente_token');
    localStorage.removeItem('docente_data');
    localStorage.removeItem('colegio_seleccionado');
    localStorage.removeItem('docente_colegios');
    localStorage.removeItem('pais_code');
    localStorage.removeItem('time_zone');
    localStorage.removeItem('nombre_colegio');
  }
};

export default { docenteApi, horariosApi, asistenciaApi, materiasApi, horariosGridApi, authHelpers };
